/**
 * Bidirectional mapping between Settings → Practices & Users UI models
 * (Practice, User) and Medplum/FHIR resources (Organization, Practitioner, Patient).
 */

import { formatHumanName } from '@medplum/core';
import type { Meta, Organization, Patient, Practitioner } from '@medplum/fhirtypes';
import { EXTENSION_URLS } from '../features/rbac/constants';
import { ADMIN_EXTENSION_URLS } from './adminDirectoryConstants';
import { ACCOUNT_STATUS_TAG_SYSTEM, PENDING_ACTIVATION_TAG_CODE } from '@unify/fhir-model/constants';
import { formatPractitionerNameParts, getRoles } from '@unify/fhir-model/resources/practitioner';
import { derivePrimaryRole } from '@unify/fhir-model/role-display';
import type { Practice, Role, User } from '../pages/users/types/rbac';

function hasPendingActivationTag(resource: Practitioner | Patient): boolean {
  return resource.meta?.tag?.some(
    (t) => t.system === ACCOUNT_STATUS_TAG_SYSTEM && t.code === PENDING_ACTIVATION_TAG_CODE,
  ) ?? false;
}

function deriveStatus(resource: Practitioner | Patient): User['status'] {
  if (hasPendingActivationTag(resource)) return 'Pending';
  if (resource.active === false) return 'Inactive';
  return 'Active';
}

import {
  PRACTICE_LEGAL_NAME_EXT,
  EIN_IDENTIFIER_SYSTEM,
  NPI_IDENTIFIER_SYSTEM,
  BRAND_PRIMARY_COLOR_EXT,
  BRAND_LOGO_URL_EXT,
  BRAND_SECONDARY_COLOR_EXT,
  PRACTICE_BIO_EXT,
  PRACTICE_INTAKE_LINK_ENABLED_EXT,
  PRACTICE_INTAKE_LINK_TOKEN_EXT,
  PRACTICE_INTAKE_PREFERENCE_EXT,
  PRACTICE_DIRECTORY_EMBED_ENABLED_EXT,
  PRACTICE_PORTAL_TILES_EXT,
  PRACTICE_CANCELLATION_WINDOW_HOURS_EXT,
  MISSED_SESSION_FEE_EXT,
  PRACTICE_EMAIL_RESPONSE_TIMEFRAME_EXT,
} from '@unify/fhir-model/constants';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extensionValueString(
  resource: { extension?: { url?: string; valueString?: string }[] },
  url: string,
): string | undefined {
  return resource.extension?.find((e) => e.url === url)?.valueString;
}

function extensionValueReference(
  resource: { extension?: { url?: string; valueReference?: { reference?: string; display?: string } }[] },
  url: string,
): { reference?: string; display?: string } | undefined {
  return resource.extension?.find((e) => e.url === url)?.valueReference;
}

const VALID_ROLES: Role[] = [
  'unify-admin', 'unify-assistant',
  'practice-owner', 'practice-admin', 'practice-assistant',
  'supervisor', 'provider', 'client',
];

function parseRole(value: string | undefined, fallback: Role): Role {
  if (value && (VALID_ROLES as string[]).includes(value)) return value as Role;
  return fallback;
}

function formatMetaDate(iso: string | undefined): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

/** Practice shown on profile/directory headers. Unify staff with no org resolve to "Unify". */
export function directoryPracticeLabel(opts: {
  role: Role;
  organizationId?: string;
  organizationName?: string;
}): { practiceId: string; practiceName: string } {
  const practiceName = opts.organizationName?.trim();
  if (practiceName) {
    return { practiceId: opts.organizationId ?? '', practiceName };
  }
  if (opts.role === 'unify-admin' || opts.role === 'unify-assistant') {
    return { practiceId: opts.organizationId ?? '', practiceName: 'Unify' };
  }
  return { practiceId: opts.organizationId ?? '', practiceName: '' };
}

/** Organization compartment refs on a profile (`meta.accounts` / legacy `meta.account`). */
function organizationRefsFromMeta(meta: Meta | undefined): string[] {
  const refs: string[] = [];
  const seen = new Set<string>();
  const add = (ref?: { reference?: string }): void => {
    const key = ref?.reference;
    if (!key?.startsWith('Organization/') || seen.has(key)) return;
    seen.add(key);
    refs.push(key);
  };
  for (const account of meta?.accounts ?? []) {
    add(account);
  }
  add(meta?.account);
  return refs;
}

/** Staff member belongs to a practice via org extension and/or Organization compartment. */
export function practitionerBelongsToOrganization(
  practitioner: Practitioner,
  organizationId: string,
): boolean {
  const orgRef = `Organization/${organizationId}`;
  const extRef = extensionValueReference(practitioner, ADMIN_EXTENSION_URLS.organization)?.reference;
  if (extRef === orgRef) return true;
  return organizationRefsFromMeta(practitioner.meta).includes(orgRef);
}

/** Practices a staff member belongs to — org extension plus Organization compartment. */
export function organizationIdsFromPractitioner(practitioner: Practitioner): string[] {
  const refs = new Set(organizationRefsFromMeta(practitioner.meta));
  const extRef = extensionValueReference(practitioner, ADMIN_EXTENSION_URLS.organization)?.reference;
  if (extRef?.startsWith('Organization/')) refs.add(extRef);
  return [...refs].map((ref) => ref.replace(/^Organization\//, ''));
}

/** Client belongs to a practice via Organization compartment on Patient.meta. */
export function patientBelongsToOrganization(patient: Patient, organizationId: string): boolean {
  const orgRef = `Organization/${organizationId}`;
  return organizationRefsFromMeta(patient.meta).includes(orgRef);
}

export function organizationIdsFromPatientMeta(patient: Patient): string[] {
  return organizationRefsFromMeta(patient.meta).map((ref) => ref.replace(/^Organization\//, ''));
}

// ---------------------------------------------------------------------------
// Organization ↔ Practice
// ---------------------------------------------------------------------------

function extensionValueBoolean(
  resource: { extension?: { url?: string; valueBoolean?: boolean }[] },
  url: string,
): boolean | undefined {
  return resource.extension?.find((e) => e.url === url)?.valueBoolean;
}

function extensionValueInteger(
  resource: { extension?: { url?: string; valueInteger?: number }[] },
  url: string,
): number | undefined {
  return resource.extension?.find((e) => e.url === url)?.valueInteger;
}

function parsePortalTiles(raw: string | undefined): Practice['portalTiles'] {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as NonNullable<Practice['portalTiles']>;
  } catch {
    return undefined;
  }
}

export function organizationToPractice(
  org: Organization,
  userCount: number,
): Practice {
  const ownerRef = extensionValueReference(org, ADMIN_EXTENSION_URLS.practiceOwner);

  const legalName = extensionValueString(org, PRACTICE_LEGAL_NAME_EXT);
  const email = org.telecom?.find((t) => t.system === 'email')?.value;
  const phone = org.telecom?.find((t) => t.system === 'phone')?.value;
  const websiteUrl = org.telecom?.find((t) => t.system === 'url')?.value;
  const cancellationWindowHours = extensionValueInteger(org, PRACTICE_CANCELLATION_WINDOW_HOURS_EXT);
  const missedSessionFee = extensionValueString(org, MISSED_SESSION_FEE_EXT);
  const emailResponseTimeframe = extensionValueString(org, PRACTICE_EMAIL_RESPONSE_TIMEFRAME_EXT);

  const ein = org.identifier?.find((i) => i.system === EIN_IDENTIFIER_SYSTEM)?.value;
  const npi = org.identifier?.find((i) => i.system === NPI_IDENTIFIER_SYSTEM)?.value;

  const addr = org.address?.[0];
  const legalEntityAddress = addr
    ? {
        addressLine1: addr.line?.[0],
        addressLine2: addr.line?.[1],
        city: addr.city,
        state: addr.state,
        zipCode: addr.postalCode,
      }
    : undefined;

  const primaryColor = extensionValueString(org, BRAND_PRIMARY_COLOR_EXT);
  const logoUrl = extensionValueString(org, BRAND_LOGO_URL_EXT);
  const secondaryColor = extensionValueString(org, BRAND_SECONDARY_COLOR_EXT);
  const bio = extensionValueString(org, PRACTICE_BIO_EXT);
  const portalTiles = parsePortalTiles(extensionValueString(org, PRACTICE_PORTAL_TILES_EXT));
  const intakeLinkEnabled = extensionValueBoolean(org, PRACTICE_INTAKE_LINK_ENABLED_EXT);
  const intakeLinkToken = extensionValueString(org, PRACTICE_INTAKE_LINK_TOKEN_EXT);
  const intakePreference = extensionValueString(org, PRACTICE_INTAKE_PREFERENCE_EXT) as Practice['intakePreference'];
  const directoryEmbedEnabled = extensionValueBoolean(org, PRACTICE_DIRECTORY_EMBED_ENABLED_EXT);

  const branding = (primaryColor || logoUrl || secondaryColor)
    ? {
        primaryColor: primaryColor ?? '#212b36',
        secondaryColor: secondaryColor ?? '#10b981',
        ...(logoUrl ? { logoUrl } : {}),
      }
    : undefined;

  return {
    id: org.id ?? '',
    name: org.name ?? '',
    ownerId: ownerRef?.reference?.replace('Practitioner/', '') ?? '',
    ownerName: ownerRef?.display ?? '—',
    userCount,
    createdAt: formatMetaDate(org.meta?.lastUpdated),
    status: org.active === false ? 'Inactive' : 'Active',
    ...(legalName ? { legalName } : {}),
    ...(email ? { email } : {}),
    ...(phone ? { phone } : {}),
    ...(websiteUrl ? { websiteUrl } : {}),
    ...(cancellationWindowHours !== undefined ? { cancellationWindowHours } : {}),
    ...(missedSessionFee ? { missedSessionFee } : {}),
    ...(emailResponseTimeframe ? { emailResponseTimeframe } : {}),
    ...(ein ? { taxId: ein } : {}),
    ...(npi ? { npi } : {}),
    ...(legalEntityAddress ? { legalEntityAddress } : {}),
    ...(bio ? { bio } : {}),
    ...(branding ? { branding } : {}),
    ...(portalTiles ? { portalTiles } : {}),
    ...(intakeLinkEnabled !== undefined ? { intakeLinkEnabled } : {}),
    ...(intakeLinkToken ? { intakeLinkToken } : {}),
    ...(intakePreference ? { intakePreference } : {}),
    ...(directoryEmbedEnabled !== undefined ? { directoryEmbedEnabled } : {}),
  };
}

// ---------------------------------------------------------------------------
// Practitioner ↔ Provider/Staff User
// ---------------------------------------------------------------------------

/** Map directory `Practice[]` rows to the structure expected by `practitionerToUser` / `patientToUser`. */
export function practicesToUserMap(practices: Practice[]): Map<string, { id: string; name: string }> {
  const m = new Map<string, { id: string; name: string }>();
  for (const pr of practices) {
    m.set(pr.id, { id: pr.id, name: pr.name });
  }
  return m;
}

export function practitionerToUser(
  p: Practitioner,
  practiceMap: Map<string, { id: string; name: string }>,
): User | null {
  if (!p.id) return null;
  const heldRoles = getRoles(p)
    .map((code) => parseRole(code, 'provider'))
    .filter((code, index, all) => all.indexOf(code) === index);
  const legacyRaw = extensionValueString(p, EXTENSION_URLS.role);
  const legacyRole = legacyRaw ? parseRole(legacyRaw, 'provider') : undefined;
  const role =
    derivePrimaryRole(heldRoles)
    ?? legacyRole
    ?? (p.active !== false ? 'provider' : 'practice-assistant');
  const parts = formatPractitionerNameParts(p);
  const name = parts.primary || 'Unknown';
  const email = p.telecom?.find((t) => t.system === 'email')?.value ?? '';
  const lastUp = p.meta?.lastUpdated;

  const orgRef = extensionValueReference(p, ADMIN_EXTENSION_URLS.organization);
  const orgId = orgRef?.reference?.replace('Organization/', '') ?? '';
  const practice = practiceMap.get(orgId);
  const labeled = directoryPracticeLabel({
    role,
    organizationId: practice?.id ?? orgId,
    organizationName: practice?.name ?? orgRef?.display,
  });

  return {
    id: p.id,
    name,
    ...(parts.legal ? { legalName: parts.legal } : {}),
    email,
    phone: p.telecom?.find((t) => t.system === 'phone')?.value,
    role,
    heldRoles,
    practiceId: labeled.practiceId,
    practiceName: labeled.practiceName,
    status: deriveStatus(p),
    createdAt: formatMetaDate(lastUp),
    // Keep the FHIR instant so callers can format in the browser local zone.
    lastActive: lastUp?.trim() ? lastUp : '—',
  };
}

/** Map a pilot Patient to a directory User (clients are not tied to a single practice). */
export function patientToUser(p: Patient): User | null {
  if (!p.id) return null;
  const role = parseRole(extensionValueString(p, EXTENSION_URLS.role), 'client');
  const name = p.name?.[0]?.text?.trim() || formatHumanName(p.name?.[0]) || 'Unknown';
  const email = p.telecom?.find((t) => t.system === 'email')?.value ?? '';
  const lastUp = p.meta?.lastUpdated;

  return {
    id: p.id,
    name,
    email,
    phone: p.telecom?.find((t) => t.system === 'phone')?.value,
    role,
    heldRoles: [role],
    practiceId: '',
    practiceName: '',
    status: deriveStatus(p),
    createdAt: formatMetaDate(lastUp),
    lastActive: lastUp?.trim() ? lastUp : '—',
  };
}

/** Normalize a FHIR Reference or literal `ResourceType/id` string. */
export function fhirReferenceString(
  ref: string | { reference?: string } | undefined,
): string | undefined {
  if (!ref) return undefined;
  if (typeof ref === 'string') return ref;
  return ref.reference;
}

/** Patient ids with at least one pilot ServiceRequest whose performer is the given Organization. */
export function organizationIdFromServiceRequestPerformer(sr: {
  performer?: Array<{ reference?: string } | string>;
  subject?: { reference?: string } | string;
}): string | undefined {
  const ref = fhirReferenceString(sr.performer?.[0]);
  if (!ref?.startsWith('Organization/')) return undefined;
  return ref.replace('Organization/', '');
}

export interface CreateUserPayload {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: Role;
  additionalRoles?: Role[];
  practiceId: string;
  practiceName: string;
}

function buildUserExtensions(role: Role, orgId: string, orgName: string) {
  return [
    { url: EXTENSION_URLS.role, valueString: role },
    {
      url: ADMIN_EXTENSION_URLS.organization,
      valueReference: {
        reference: `Organization/${orgId}`,
        display: orgName,
      },
    },
  ];
}

export function userPayloadToPractitioner(payload: CreateUserPayload): Practitioner {
  return {
    resourceType: 'Practitioner',
    name: [{ use: 'official', given: [payload.firstName], family: payload.lastName }],
    telecom: [
      { system: 'email', use: 'work', value: payload.email },
      ...(payload.phone ? [{ system: 'phone' as const, use: 'work' as const, value: payload.phone }] : []),
    ],
    active: true,
    extension: buildUserExtensions(payload.role, payload.practiceId, payload.practiceName),
  };
}

export function userPayloadToPatient(payload: CreateUserPayload): Patient {
  return {
    resourceType: 'Patient',
    name: [{ use: 'official', given: [payload.firstName], family: payload.lastName }],
    telecom: [
      { system: 'email', use: 'home', value: payload.email },
      ...(payload.phone ? [{ system: 'phone' as const, use: 'home' as const, value: payload.phone }] : []),
    ],
    active: true,
    extension: [{ url: EXTENSION_URLS.role, valueString: payload.role }],
  };
}
